import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { ILanguage } from 'app/shared/model/language.model';
import { getEntities as getLanguages } from 'app/entities/language/language.reducer';
import { IPrompt } from 'app/shared/model/prompt.model';
import { getEntity, updateEntity, createEntity, reset } from './prompt.reducer';

export const PromptUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const languages = useAppSelector(state => state.language.entities);
  const promptEntity = useAppSelector(state => state.prompt.entity);
  const loading = useAppSelector(state => state.prompt.loading);
  const updating = useAppSelector(state => state.prompt.updating);
  const updateSuccess = useAppSelector(state => state.prompt.updateSuccess);

  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/prompt' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({});
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getLanguages({}));
  }, []);

  useEffect(() => {
    if (promptEntity) {
      setFormData({
        ...promptEntity,
        language: promptEntity?.language?.id,
      });
    }
  }, [promptEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.key || formData.key.trim() === '') {
      newErrors.key = 'Key is required';
    }
    if (!formData.language) {
      newErrors.language = 'Language is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    const entity = {
      ...promptEntity,
      ...values,
      language: languages.find(it => it.id.toString() === values.language?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.prompt.home.createOrEditLabel">Create or edit a Prompt</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField id="prompt-id" name="id" label={translate('global.field.id')} value={formData.id || ''} fullWidth InputProps={{ readOnly: true }} />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.prompt.key')}
              id="prompt-key"
              name="key"
              data-cy="key"
              value={formData.key || ''}
              onChange={handleChange('key')}
              fullWidth
              required
              error={!!errors.key}
              helperText={errors.key || ''}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.prompt.value')}
              id="prompt-value"
              name="value"
              data-cy="value"
              value={formData.value || ''}
              onChange={handleChange('value')}
              fullWidth
            />
            <FormControl fullWidth required error={!!errors.language}>
              <InputLabel id="prompt-language-label">{translate('jhipsterSampleApplicationApp.prompt.language')}</InputLabel>
              <Select
                labelId="prompt-language-label"
                id="prompt-language"
                name="language"
                data-cy="language"
                value={formData.language || ''}
                label={translate('jhipsterSampleApplicationApp.prompt.language')}
                onChange={handleSelectChange('language')}
              >
                <MenuItem value="" key="0">
                  <em>Select...</em>
                </MenuItem>
                {languages
                  ? languages.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/prompt"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PromptUpdate;
